import { BlogCard } from './types';

export const walkInGpBlogCards: BlogCard[] = [
  {
    title: {
      content:
        '### Walk-In GP Near Me in London: What to Expect at a Private Clinic',
      styles: {
        h3: 'font-Mulish font-bold text-royal-875 my-3 text-base md:text-lg leading-tight line-clamp-2',
      },
      topicsStyles: {
        h3: 'text-theme-primary text-3xl md:text-4xl my-4 md:my-6 p-2 font-black',
      },
    },
    description: {
      content:
        'A walk-in GP clinic gives you access to a qualified private doctor without a pre-booked appointment, a referral, or prior registration.',
      styles: {
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
      },
    },
    imageUrl: 'walk-in-gp-blog.webp',
    bannerUrl: 'gp-blog-cover-2.webp',
    bannerDescription:
      'A walk-in GP clinic gives you access to a qualified private doctor without a pre-booked appointment, a referral, or prior registration. For patients in London searching for a walk in gp near me, LIPS Healthcare at Battersea Power Station offers same-day private GP consultations seven days a week, including evenings and weekends. This guide explains how private walk-in GP clinics work, what they can treat, and what to expect when you arrive.',
    slug: 'walk-in-gp-near-me-in-london',
    contentType: 'article',
    speciality: 'GP - General Practice',
    date: {
      content: '15, September 2026',
      iconUrl: '/images/blogs/calendar.svg',
      styles: {
        p: 'text-gray-500 text-xs',
      },
    },
    author: {
      content: 'LIPS Healthcare Marketing Team',
      styles: {
        p: 'text-indigo-650 font-bold text-xs',
        h3: 'text-theme-primary font-black text-2xl md:text-4xl',
      },
    },
    tableOfContents: {
      heading: {
        content: '### TABLE OF CONTENTS:',
        styles: {
          h3: 'font-Mulish font-bold text-royal-875 mb-3 text-lg md:text-xl',
        },
      },
      content: [
        {
          text: 'What is a walk-in GP and how does it work?',
          id: 'what-is-a-walk-in-gp',
        },
        {
          text: 'Walk-in GP in London: what to expect at a private clinic',
          id: 'walk-in-gp-in-london-what-to-expect',
        },
        {
          text: 'What conditions can a walk-in GP treat?',
          id: 'what-conditions-can-a-walk-in-gp-treat',
        },
        {
          text: 'Walk-in GP vs same-day appointment: which is right for you?',
          id: 'walk-in-gp-vs-same-day-appointment',
        },
        {
          text: 'Cannot make it in? Private GP by telephone, video, or home visit',
          id: 'cannot-make-it-in-private-gp-options',
        },
        {
          text: 'Walk-in private GP near me: South London access',
          id: 'walk-in-private-gp-near-me-south-london',
        },
        {
          text: 'Frequently asked questions about walk-in GP in London',
          id: 'frequently-asked-questions',
        },
      ],
      styles: {
        p: 'text-lg text-teal-500 underline leading-snug text-left cursor-pointer font-semibold',
      },
    },
    content: {
      content: `

<span id="what-is-a-walk-in-gp"></span>

### What is a walk-in GP and how does it work?

A walk-in GP clinic allows you to see a qualified doctor without booking in advance. You attend when you need to, and you are seen by a clinician on the same day.

At a private walk-in GP clinic, there is no requirement to be registered as a patient beforehand. Most private walk-in GP clinics in London operate on a first-come, first-seen or clinical-urgency basis. At LIPS Healthcare in Turbine Hall B, Battersea Power Station, most walk-in patients are seen within the hour.

<div align="center">\n\n
![Walk-in GP at LIPS Healthcare](paying-privately-blog.webp "600x400")\n\n
</div>

The consultation covers a full clinical assessment, a diagnosis where possible, and a clear next step. Whether that is a prescription, a blood test, or a referral to a specialist, you leave with a plan and not a request to come back another day.

<span id="walk-in-gp-in-london-what-to-expect"></span>

### Walk-in GP in London: what to expect at a private clinic

Private walk-in GP clinics in London offer a faster, more structured experience than most patients expect. You are seen by a GMC-registered physician with specialist training in general practice. Consultations are conducted in a private room with full clinical resources available.

<div align="center">\n\n
![What to expect at a private walk-in GP clinic](expect-private-clinic.webp "600x400")\n\n
</div>

At LIPS Healthcare in Turbine Hall B, Battersea Power Station, walk-in consultations include access to on-site blood tests, an in-house pharmacy, and same-day specialist referrals where your concern requires escalation. Patients from Chelsea, Vauxhall, Clapham, and Pimlico are seen seven days a week, with direct access via the Northern line or London Overground.

No appointment needed. No registration. No referral. You walk in, and you are seen by a consultant-led clinician on the same day.

<div style="text-align:center;margin:2rem 0;"><strong><a href="/private-gp#booking-calendar-widget" target="_blank" rel="noopener noreferrer">Walk in or book at LIPS Healthcare, Battersea Power Station</a></strong></div>

<span id="what-conditions-can-a-walk-in-gp-treat"></span>

### What conditions can a walk-in GP treat?

Walk-in GP clinics cover a wide range of presentations. If the concern would be appropriate at a standard GP visit, it is appropriate at a walk-in private GP clinic.

*Common reasons patients attend a walk in private gp near me include:*

<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,300px),1fr));gap:20px;margin:1.5rem 0;width:100%;align-items:stretch;">

  <div style="min-width:0;border-radius:16px;background:#fff;padding:20px;box-shadow:0px 2px 4px 0px #0000001F;">
    <h4 style="margin:0 0 12px;font-size:16px;font-weight:700;color:#1E2A8A;">Infections</h4>
    <div style="display:flex;flex-wrap:wrap;gap:8px;">
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">Sore Throat</span>
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">Ear Infections</span>
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">Urinary Tract Infections</span>
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">Skin Infections</span>
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">Chest Infections</span>
    </div>
  </div>

  <div style="min-width:0;border-radius:16px;background:#fff;padding:20px;box-shadow:0px 2px 4px 0px #0000001F;">
    <h4 style="margin:0 0 12px;font-size:16px;font-weight:700;color:#1E2A8A;">Acute pain</h4>
    <div style="display:flex;flex-wrap:wrap;gap:8px;">
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">Back Pain</span>
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">Joint Pain</span>
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">Headache</span>
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">Musculoskeletal Complaints Needing Prompt Assessment</span>
    </div>
  </div>

  <div style="min-width:0;border-radius:16px;background:#fff;padding:20px;box-shadow:0px 2px 4px 0px #0000001F;">
    <h4 style="margin:0 0 12px;font-size:16px;font-weight:700;color:#1E2A8A;">Mental health concerns</h4>
    <div style="display:flex;flex-wrap:wrap;gap:8px;">
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">Anxiety</span>
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">Low Mood</span>
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">Stress-Related Symptoms</span>
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">Requests For Same-Day Mental Health Support Or Referral</span>
    </div>
  </div>

  <div style="min-width:0;border-radius:16px;background:#fff;padding:20px;box-shadow:0px 2px 4px 0px #0000001F;">
    <h4 style="margin:0 0 12px;font-size:16px;font-weight:700;color:#1E2A8A;">Skin conditions</h4>
    <div style="display:flex;flex-wrap:wrap;gap:8px;">
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">Rashes</span>
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">Insect Bites</span>
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">Minor Wound Assessment</span>
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">Allergic Reactions</span>
    </div>
  </div>

  <div style="min-width:0;border-radius:16px;background:#fff;padding:20px;box-shadow:0px 2px 4px 0px #0000001F;">
    <h4 style="margin:0 0 12px;font-size:16px;font-weight:700;color:#1E2A8A;">Prescriptions and sick notes</h4>
    <div style="display:flex;flex-wrap:wrap;gap:8px;">
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">Fit Notes</span>
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">Private Prescriptions</span>
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">Including Same-Day Issue</span>
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">Repeat Medication Requests</span>
    </div>
  </div>

  <div style="min-width:0;border-radius:16px;background:#fff;padding:20px;box-shadow:0px 2px 4px 0px #0000001F;">
    <h4 style="margin:0 0 12px;font-size:16px;font-weight:700;color:#1E2A8A;">Stomach complaints</h4>
    <div style="display:flex;flex-wrap:wrap;gap:8px;">
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">Nausea, Vomiting</span>
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">Diarrhoea</span>
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">Abdominal Discomfort</span>
    </div>
  </div>

</div>

If you are uncertain whether your condition warrants a walk-in visit, attend. A same-day clinical assessment is always preferable to waiting.

<span id="walk-in-gp-vs-same-day-appointment"></span>

### Walk-in GP vs same-day appointment: which is right for you?

Both are same-day services. The practical difference is whether you want a confirmed time slot.

A same-day appointment gives you a confirmed slot at a set time. The clinician has your details before you arrive and the consultation is uninterrupted. A walk-in visit has no fixed time. You attend, you are seen in order, and the wait depends on how busy the clinic is when you arrive.

For most routine concerns, either option works well. If you need certainty around timing, book a same-day appointment. If you need to be seen immediately and cannot wait to secure a slot, walk in. At LIPS Healthcare in Battersea and Nine Elms, both services are available at the same site, seven days a week.

<div align="center">\n\n
![Walk-in GP vs same-day appointment](same-day-standard-private-gp.webp "600x400")\n\n
</div>

<span id="cannot-make-it-in-private-gp-options"></span>

### Cannot make it in? Private GP by telephone, video, or home visit

Walking in is not your only option. If getting to LIPS Healthcare at Battersea Power Station is not possible on the day, a private GP consultation by telephone or video is available seven days a week, including evenings and weekends.

<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,280px),1fr));gap:24px;margin:2rem 0;width:100%;align-items:stretch;">

  <div style="min-width:0;border-radius:16px;background:#fff;padding:24px;box-shadow:0px 2px 4px 0px #0000001F;">
    <div class="mb-4 flex h-[168px] w-full items-center justify-center overflow-hidden [&_span]:flex [&_span]:h-full [&_span]:w-full [&_span]:items-center [&_span]:justify-center [&_span]:overflow-hidden [&_img]:!h-auto [&_img]:!max-h-full [&_img]:!max-w-full [&_img]:object-contain">
      <img src="telephone-and-video-consultations.webp" alt="Telephone and video consultations" title="480x240" />
    </div>
    <h4 style="margin:0 0 12px;font-size:20px;font-weight:700;color:#1E2A8A;text-align:center;">Telephone and video consultations</h4>
    <p style="margin:0 0 10px;font-size:14px;line-height:1.6;text-align:left;">Telephone consultations are appropriate for prescription queries, medication reviews, results interpretation, and many acute concerns that do not require a physical examination. A video consultation works in the same way, with the advantage of allowing your GP to see any visible symptoms directly. Rashes, swollen joints, skin changes, and similar presentations are often assessed effectively over video without any need to travel.</p>
  </div>

  <div style="min-width:0;border-radius:16px;background:#fff;padding:24px;box-shadow:0px 2px 4px 0px #0000001F;">
    <div class="mb-4 flex h-[168px] w-full items-center justify-center overflow-hidden [&_span]:flex [&_span]:h-full [&_span]:w-full [&_span]:items-center [&_span]:justify-center [&_span]:overflow-hidden [&_img]:!h-auto [&_img]:!max-h-full [&_img]:!max-w-full [&_img]:object-contain">
      <img src="home-visits.webp" alt="Home visits" title="480x240" />
    </div>
    <h4 style="margin:0 0 12px;font-size:20px;font-weight:700;color:#1E2A8A;text-align:center;">Home visits</h4>
    <p style="margin:0 0 10px;font-size:14px;line-height:1.6;text-align:left;">Home visits are available for patients in the local area who are unwell and unable to leave home. LIPS Healthcare GPs travel to patients in Battersea, Nine Elms, Vauxhall, and nearby areas when attending the clinic is not practical. Contact the clinic directly to confirm availability and arrange a same-day visit.</p>
  </div>

</div>

All three formats, walk-in, telephone, and video, are available seven days a week. There is no need to register in advance, and no GP referral is required. If you are looking for a private GP near you in South London and walking in is not an option today, the same clinical service is still accessible without leaving home.

<span id="walk-in-private-gp-near-me-south-london"></span>

### Walk-in private GP near me: South London access

For patients in South and Central London searching for a walk in gp near me, LIPS Healthcare at Battersea Power Station is the nearest consultant-led private GP clinic with no registration, no referral, and no waiting list.

The clinic is located on the first floor, Turbine Hall B, Battersea Power Station. It is accessible from Wandsworth, Nine Elms, Pimlico, Vauxhall, Chelsea, and Clapham via the Northern line or London Overground. Walk-in patients are seen seven days a week including evenings and weekends.

<div align="center">\n\n
![Walk-in private GP near me in South London](private-prescription-cost.webp "600x400")\n\n
</div>

For patients looking for private GP services near me or a private GP clinic near me in South London, LIPS Healthcare offers on-site blood tests, an in-house pharmacy, and direct access to more than 170 specialist consultants, all under one roof.


><span style="font-weight: bold; color: #2A347A;">Medical disclaimer</span>
>
>This article is for informational purposes only. It does not constitute medical advice, a diagnosis, or a treatment recommendation. Always consult a qualified medical professional for any health concern. Pricing ranges are approximate and subject to change. Contact LIPS Healthcare directly for current fees.


<span id="frequently-asked-questions"></span>
`,
      styles: {
        h3: 'text-lg md:text-[32px] font-bold text-theme-primary mb-4',
        h4: 'text-lg md:text-[20px] font-bold text-theme-primary mb-4',
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
        a: 'font-semibold text-text-action',
        em: 'not-italic font-bold',
        strong:
          'inline-block bg-theme-primary hover:bg-white border border-theme-primary text-white px-6 py-3 rounded-lg font-bold no-underline cursor-pointer my-4 text-center [&_a]:text-white [&_a]:no-underline [&_a]:font-bold hover:[&_a]:text-theme-primary',
        table:
          'w-full table-fixed border-collapse text-xs md:text-sm border-2 border-black',
        th: 'border-2 border-black bg-text-muted-blue px-2 py-2 md:px-4 md:py-3 text-left font-semibold text-theme-primary whitespace-normal wrap-break-word',
        td: 'border-2 border-black px-2 py-2 md:px-4 md:py-3 align-top font-semibold whitespace-normal wrap-break-word',
        ul: 'rich-check-list flex flex-col gap-1',
        li: 'inline-flex w-fit items-center rounded-xl bg-white px-4 py-3 text-base [box-shadow:0px_2px_8px_0px_#00000014]',
        blockquote:
          'border-l-4 border-theme-primary bg-[#E0E7FF] rounded-lg p-4 my-8 text-sm text-text-default not-italic',
      },
      tableOptions: {
        width: '100%',
        columnWidths: ['50%', '50%'],
      },
    },
    faq: {
      heading: {
        content: '#### Frequently asked questions about walk-in GP in London',
        styles: {
          h4: 'text-xl md:text-2xl font-bold text-theme-primary mb-6',
        },
      },
      items: [
        {
          question: {
            content: 'Is there a walk-in GP near me in London?',
          },
          answer: {
            content:
              'Yes. LIPS Healthcare offers a private walk-in GP service at Battersea Power Station, accessible via the Northern Line and London Overground from Chelsea, Clapham, Vauxhall, Pimlico, Nine Elms, and Wandsworth. No prior registration or GP referral is required.',
          },
        },
        {
          question: {
            content: 'How does a walk-in GP clinic work?',
          },
          answer: {
            content:
              'You attend the clinic without a pre-booked appointment. A clinician assesses your concern on the same day, provides a diagnosis where possible, and confirms your next step. At LIPS Healthcare, most walk-in patients are seen within the hour.',
          },
        },
        {
          question: {
            content: 'How much does a walk-in GP cost in London?',
          },
          answer: {
            content:
              'Between £80 and £200 for a standard consultation at most private walk-in GP clinics, depending on what is included. At LIPS Healthcare, pricing is transparent with no registration fee. Contact the clinic directly for current fees.',
          },
        },
        {
          question: {
            content:
              'What is the difference between a walk-in GP and a same-day appointment?',
          },
          answer: {
            content:
              'A same-day appointment gives you a confirmed time slot with a named physician. A walk-in visit has no fixed time, and you are seen in order of arrival or clinical urgency. Both services are available at LIPS Healthcare, Battersea Power Station, seven days a week.',
          },
        },
        {
          question: {
            content:
              'When should I go to a walk-in GP rather than emergency services?',
          },
          answer: {
            content:
              'A walk-in GP is the right choice for non-life-threatening conditions requiring same-day assessment: infections, minor injuries, acute pain, prescriptions, and mental health support. If you are experiencing chest pain, difficulty breathing, stroke symptoms, severe blood loss, or any life-threatening emergency, call 999 immediately. Do not attend a walk-in clinic for emergencies.',
          },
        },
        {
          question: {
            content: 'Do I need to register to use a walk-in GP?',
          },
          answer: {
            content:
              'No. LIPS Healthcare accepts walk-in patients without prior registration. You do not need to be an existing patient or to complete any paperwork before your visit.',
          },
        },
        {
          question: {
            content: 'Can I get a prescription at a walk-in GP clinic?',
          },
          answer: {
            content:
              'Yes. LIPS Healthcare GPs can issue private prescriptions at a walk-in consultation. The on-site pharmacy at Battersea Power Station means your prescription can be filled immediately after your appointment, with no separate pharmacy visit required.',
          },
        },
        {
          question: {
            content:
              'Can I see a LIPS Healthcare GP without coming to the clinic?',
          },
          answer: {
            content:
              'Yes. Telephone and video consultations are available seven days a week for eligible concerns. Home visits are also available for patients who are unable to travel. Contact LIPS Healthcare directly to confirm the right format for your situation.',
          },
        },
      ],
    },
  },
];
